import { onBeforeUnmount, onMounted, nextTick, ref } from 'vue';

// Body selectors used by ant-design-vue tables with horizontal scroll.
const TABLE_BODY_SELECTORS = [
  '.ant-table-body',
  '.ant-table-content',
  '.ant-table-container'
];

export const findTableBody = (host) => {
  if (!host) return null;
  for (const selector of TABLE_BODY_SELECTORS) {
    const body = host.querySelector(selector);
    if (body) return body;
  }
  return null;
};

/**
 * Keeps a scrollbar placed above an ant-design-vue table in sync with the
 * table's own horizontal scroll (same behavior as tickets-table-test.vue).
 *
 * Usage: bind `topRef` to the top scroll container, `hostRef` to the element
 * wrapping the table, call `onTopScroll` on the top container scroll event and
 * `refresh` after the table content changes.
 */
export default function useTopScrollSync() {
  const topRef = ref(null);
  const hostRef = ref(null);
  const scrollWidth = ref(0);
  const visibleWidth = ref(0);
  const hasOverflow = ref(false);

  let tableBody = null;
  let resizeObserver = null;

  const measure = () => {
    if (!tableBody) return;
    scrollWidth.value = Math.max(tableBody.scrollWidth, tableBody.clientWidth);
    visibleWidth.value = tableBody.clientWidth;
    hasOverflow.value = tableBody.scrollWidth > tableBody.clientWidth + 1;
  };

  const onBodyScroll = () => {
    if (topRef.value && topRef.value.scrollLeft !== tableBody.scrollLeft) {
      topRef.value.scrollLeft = tableBody.scrollLeft;
    }
  };

  const onTopScroll = () => {
    if (tableBody && tableBody.scrollLeft !== topRef.value.scrollLeft) {
      tableBody.scrollLeft = topRef.value.scrollLeft;
    }
  };

  const detach = () => {
    if (tableBody) tableBody.removeEventListener('scroll', onBodyScroll);
    if (resizeObserver) resizeObserver.disconnect();
    tableBody = null;
    resizeObserver = null;
  };

  const attach = () => {
    const body = findTableBody(hostRef.value);
    if (!body) return;
    if (body !== tableBody) {
      detach();
      tableBody = body;
      tableBody.addEventListener('scroll', onBodyScroll);
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(tableBody);
        const table = tableBody.querySelector('table');
        if (table) resizeObserver.observe(table);
      }
    }
    measure();
  };

  // Call after the table content changes (data, expanded rows, columns).
  const refresh = async () => {
    await nextTick();
    attach();
  };

  onMounted(refresh);
  onBeforeUnmount(detach);

  return {
    topRef,
    hostRef,
    scrollWidth,
    visibleWidth,
    hasOverflow,
    onTopScroll,
    refresh
  };
}
